import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta } from "@/lib/usePageMeta";
import { GENRES } from "@/lib/constants";
import { BookCard, BookCardSkeleton } from "@/components/BookCard";
import { AuthorAvatar } from "@/components/AuthorAvatar";
import { SectionHeading } from "@/components/SectionHeading";
import { AdSlot } from "@/components/AdSlot";
import { useI18n } from "@/contexts/I18nContext";

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-6xl px-4 sm:px-6 ${className}`}>{children}</div>
);

function HeroSearch({ big }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); };
  return (
    <form onSubmit={submit} role="search" className="flex items-stretch">
      <label htmlFor="home-search" className="sr-only">{t("search_placeholder")}</label>
      <input
        id="home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")}
        className={`min-w-0 flex-1 bg-card px-4 outline-none placeholder:text-muted-foreground/80 focus-visible:ring-2 focus-visible:ring-ring ${big ? "h-14 text-lg" : "h-12"}`}
        data-testid="home-search-input"
      />
      <button type="submit" aria-label={t("search_button")} className="flex items-center gap-2 bg-primary px-5 text-primary-foreground hover:bg-primary/90">
        <Search size={18} aria-hidden="true" /><span className="hidden sm:inline">{t("search_button")}</span>
      </button>
    </form>
  );
}

/* The headline's bar runs the width of the page and the search hangs from it. */
function HeroShirorekha() {
  const { t } = useI18n();
  return (
    <Container className="pt-10 sm:pt-16">
      <h1 className="max-w-4xl text-[2.15rem] leading-[1.28] sm:text-[3.6rem] sm:leading-[1.22]" data-testid="hero-title">
        {t("hero_title")}
      </h1>
      <div className="mt-3 border-t-[3px] border-foreground">
        <div className="sm:ml-auto sm:max-w-xl border-x border-b border-foreground/25">
          <HeroSearch big />
        </div>
      </div>
      <p className="mt-5 max-w-xl text-muted-foreground reading">{t("hero_subtitle")}</p>
    </Container>
  );
}

function Shelf({ books, loading }) {
  // Covers stand on a line; on phones the shelf scrolls sideways.
  const items = loading ? Array.from({ length: 6 }, (_, i) => ({ id: `s${i}` })) : books;
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:overflow-visible sm:px-0 [scrollbar-width:thin]">
      <ul className="flex snap-x gap-4 sm:grid sm:grid-cols-4 lg:grid-cols-6 sm:gap-6">
        {items.map((b) => (
          <li key={b.id} className="w-[40%] shrink-0 snap-start sm:w-auto">
            {b.slug ? <BookCard book={b} eager /> : <BookCardSkeleton />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const featured = useFetch("/books?featured=true&limit=6");
  // Nothing featured yet: fill the shelf with the top-rated books instead of showing an empty rule.
  const noneFeatured = featured.status === "ok" && !featured.data?.items?.length;
  const topRated = useFetch(noneFeatured ? "/books?sort=rating&limit=6" : null);
  const shelf = noneFeatured ? topRated : featured;
  const newest = useFetch("/books?sort=newest&limit=12");
  const authors = useFetch("/authors?sort=popular&limit=6");
  usePageMeta({});

  return (
    <div className="pb-8">
      <HeroShirorekha />

      <Container className="mt-14">
        <SectionHeading title={t("featured_books")} to="/books" linkLabel={t("all_books")} />
        <Shelf books={shelf.data?.items || []} loading={featured.status === "loading" || shelf.status === "loading"} />
        <div aria-hidden="true" className="mt-0 h-2 border-t-[3px] border-foreground/80 shadow-[0_6px_10px_-8px_hsl(var(--foreground)/0.6)] sm:-mt-px" />
      </Container>

      <Container className="mt-12">
        <SectionHeading title={t("browse_genres")} />
        <ul className="flex flex-wrap gap-2">
          {GENRES.map((g) => (
            <li key={g}>
              <Link to={`/books?genre=${encodeURIComponent(g)}`}
                className="inline-block rounded-full border border-foreground/30 px-4 py-1.5 font-reading hover:border-primary hover:text-primary">
                {g}
              </Link>
            </li>
          ))}
        </ul>
      </Container>

      <Container className="mt-14">
        <SectionHeading title={t("newest_books")} to="/books?sort=newest" linkLabel={t("see_all")} />
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4 lg:grid-cols-6 sm:gap-x-6">
          {(newest.data?.items || []).map((b, i) => (
            <li key={b.id} className={i >= 6 ? "hidden sm:block" : undefined}><BookCard book={b} /></li>
          ))}
          {newest.status === "loading" && Array.from({ length: 6 }, (_, i) => <li key={i}><BookCardSkeleton /></li>)}
        </ul>
      </Container>

      {/* Between the new books and the authors: well below the first screen at every size. */}
      {newest.status === "ok" && <Container className="mt-14"><AdSlot placement="home-mid" /></Container>}

      <Container className="mt-14">
        <SectionHeading title={t("top_authors")} to="/authors" linkLabel={t("all_authors")} />
        <ul className="grid grid-cols-3 gap-x-4 gap-y-6 sm:grid-cols-6">
          {(authors.data?.items || []).map((a) => (
            <li key={a.id}>
              <Link to={`/authors/${a.slug}`} className="group flex flex-col items-center text-center" data-testid={`author-card-${a.slug}`}>
                <AuthorAvatar src={a.photo} name={a.name_hi} className="w-20 sm:w-24" />
                <span className="mt-2 leading-snug line-clamp-2 group-hover:text-primary">
                  {lang === "hi" ? a.name_hi : (a.name_en || a.name_hi)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </Container>
    </div>
  );
}
